import React from 'react';
import ReactModal from 'react-modal';
import { CrossIcon } from 'acscp/components/common/icons/crossIcon';
import style from 'acscp/components/common/modal.module.scss';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { History } from 'history';
import { isAppHistory } from 'acscp/store/history';
import { setStatusBarForDarkContent, setStatusBarForLightContent } from 'acscp/mobile/device/statusBar';

interface ModalProps {
    modalClassName?: string;
    modalHeaderClassName?: string;
    closingModalClassName?: string;
    modalHeaderContent?: React.ReactNode;
    overlayClassName?: string;
    modalBodyClassName?: string;
    isOpen: boolean;
    closeModal: (history: History) => void;
}

/**
 * We render modals in two major ways.
 *
 * 1. Just rendering on the page with an 'if' check (or some variation of it)
 * This is a good option for a simple solution, where we have only one place that can trigger the modal,
 * and where the modal seems to be more of a temporary interruption popup that should not normally be part of the page
 * (e.g. "login is required" modal popup)
 *
 * 2. Rendering it within a Route, which means that it is shown when we transition to a particular URL.
 * The setup for this is a bit more complicated (you need to configure routes to both render the parent component and
 * the modal), so you should look for an example like the sharing modal.
 * This is a good option for a more constant logic. If you click 'Share', then you expect something to change,
 * the modal to be displayed.
 * As a bonus, this approach allows creating direct links to these modals, which might be required or just convenient.
 * WARNING: it is important to provide a correct closeModal function to modals rendered using routes,
 * such that it wouldn't mess with some transition logic in the app (e.g. "go back" links).
 * You can use @see closeModalByGoingBackIfPossibleFallingBackToUrl to do this properly.
 */
class ModalPresentational extends React.Component<ModalProps & RouteComponentProps<any>, {}> {
    componentDidUpdate(propsPrev: ModalProps): void {
        if (this.props.isOpen !== propsPrev.isOpen) {
            if (this.props.isOpen) {
                setStatusBarForDarkContent();
            } else {
                setStatusBarForLightContent(); // revert
            }
        }
    }

    render(): JSX.Element {
        return (
            <ReactModal
                className={this.props.modalClassName || style.Modal}
                isOpen={this.props.isOpen}
                overlayClassName={this.props.overlayClassName || style.Overlay}
                // TODO: ACSCP-196: fix accessibility issues
                contentLabel="Modal"
            >
                <div className={this.props.modalHeaderClassName || style.ModalHeader}>
                    {this.props.modalHeaderContent}
                    <CrossIcon
                        className={this.props.closingModalClassName || style.Cross}
                        onClick={() => this.props.closeModal(this.props.history)}
                        aria-label="Close modal window"
                    />
                </div>
                <div className={this.props.modalBodyClassName || style.ModalBody}>
                    {this.props.children}
                </div>
            </ReactModal>
        );
    }
}

const Modal = withRouter(ModalPresentational);

function closeModalByGoingBackIfPossibleFallingBackToUrl(url: string): (history: History) => void {
    return history => {
        if (isAppHistory(history) && history.canGoBackWithinApp()) {
            history.goBack();
        } else {
            history.replace(url, history.location.state);
        }
    };
}

export { Modal, closeModalByGoingBackIfPossibleFallingBackToUrl };
